import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  addEntities,
  updateEntities,
  getEntity,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IQuestion } from './question.interface';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl2-questions';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IQuestion, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class QuestionCoreService {
  private _loading = false;

  /**
   * @name questionList$
   * @description emits stored list of the companies
   */

  questionList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getQuestions(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`questions/list`, params || {})
      .subscribe((data: IQuestionListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addQuestion(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('questions/add', dataInput || {}).pipe(
      tap((data: IQuestion) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public editQuestion(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('questions/edit', dataInput || {}).pipe(
      tap(() => {
        store.update(updateEntities(dataInput['_id'], dataInput));
        this._loading = false;
      }),
    );
  }

  public deleteQuestion(data): Observable<any> {
    this._loading = true;
    return this._api.delete('questions', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading = false;
      }),
    );
  }

  public getQuestionById(questionId: string): Observable<IQuestion> {
    return this._api.get(`questions/${questionId}`);
  }

  public getQuestionInfo(id: string): IQuestion {
    const question = store.query(getEntity(id));
    return question;
  }

  public resolveQuestionId(question: string | IQuestion): string {
    let questionId: string | IQuestion = '';
    if (question) {
      questionId = question;
    }
    if (question['_id']) {
      questionId = question['_id'];
    }
    return questionId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getQuestions();
    }
  }
}

interface IQuestionListResponse {
  list: IQuestion[];
  totalCount: number;
}
